import { jsx, jsxs } from "react/jsx-runtime";
import { ZoomCircularViewSlider } from "./ZoomCircularViewSlider";
import VeWarning from "../helperComponents/VeWarning";
import Labels from "./Labels";
import SelectionLayer from "./SelectionLayer";
import Caret from "./Caret";
import Axis from "./Axis";
import Orf from "./Orf";
import Feature from "./Feature";
import Primer from "./Primer";
import Cutsite from "./Cutsite";
import sortBy from "lodash/sortBy";
import {
  normalizePositionByRangeLength,
  getPositionFromAngle,
  getRangeLength,
  getOverlapsOfPotentiallyCircularRanges,
  isRangeOrPositionWithinRange,
  getMiddleOfRange,
  getSequenceWithinRange,
  trimRangeByAnotherRange
} from "@teselagen/range-utils";
import React, { useEffect, useRef, useState } from "react";
import Draggable from "react-draggable";
import withEditorInteractions from "../withEditorInteractions";
import drawAnnotations from "./drawAnnotations";
import "./style.css";
import draggableClassnames from "../constants/draggableClassnames";
import { getOrfColor } from "../constants/orfFrameToColorMap";
import { getSingular } from "../utils/annotationTypes";
import { upperFirst, map, flatMap, noop } from "lodash-es";
import useAnnotationLimits from "../utils/useAnnotationLimits";
import {
  getClientX,
  getClientY,
  getParedDownWarning,
  pareDownAnnotations
} from "../utils/editorUtils";
import { getAllSelectionLayers } from "../utils/selectionLayer";
import classNames from "classnames";
import calculateTickMarkPositionsForGivenRange from "../utils/calculateTickMarkPositionsForGivenRange";
import { RotateCircularViewSlider } from "./RotateCircularViewSlider";
import { AxisNumbers } from "./AxisNumbers";
import { positionCutsites } from "./positionCutsites";
import { usePinch } from "@use-gesture/react";
import { CircularZoomMinimap } from "./CircularZoomMinimap";
import { normalizeAngleRange } from "./normalizeAngleRange";
import { CircularDnaSequence } from "./CircularDnaSequence";
import { VeTopRightContainer } from "./VeTopRightContainer";
import { normalizeAngle } from "./normalizeAngle";
import { defaultEnzymesByName, getCutsiteType } from "@teselagen/sequence-utils";
import {
  editorDragged,
  editorDragStarted,
  editorDragStopped
} from "../withEditorInteractions/clickAndDragUtils";
const BASE_RADIUS = 70;
function getNearestCursorPositionToMouseEvent(event, {
  updateSelectionOrCaret,
  caretPosition,
  selectionLayer,
  caretPositionUpdate,
  selectionLayerUpdate,
  sequenceData,
  sequenceLength,
  circRef,
  rotationRadians
}, callback) {
  const clientX = getClientX(event);
  const clientY = getClientY(event);
  if (!clientX) {
    return;
  }
  const boundingRect = circRef.current.getBoundingClientRect();
  const clickX = clientX - boundingRect.left - boundingRect.width / 2;
  const clickY = clientY - boundingRect.top - boundingRect.height / 2;
  let angle = Math.atan2(clickY, clickX) + Math.PI / 2 - rotationRadians;
  if (angle < 0) angle += Math.PI * 2;
  let nearestCaretPos = sequenceLength === 0 ? 0 : normalizePositionByRangeLength(
    getPositionFromAngle(angle, sequenceLength, true),
    sequenceLength
  );
  if (sequenceData && sequenceData.isProtein) {
    nearestCaretPos = Math.round(nearestCaretPos / 3) * 3;
  }
  callback({
    updateSelectionOrCaret,
    caretPosition,
    selectionLayer,
    caretPositionUpdate,
    selectionLayerUpdate,
    sequenceLength,
    event,
    doNotWrapOrigin: !(sequenceData && sequenceData.circular),
    className: (event.target.parentNode.className.animVal !== void 0 ? event.target.parentNode.className.animVal : event.target.parentNode.className) || "",
    shiftHeld: event.shiftKey,
    nearestCaretPos,
    selectionStartGrabbed: event.target.parentNode.classList.contains(
      draggableClassnames.selectionStart
    ),
    selectionEndGrabbed: event.target.parentNode.classList.contains(
      draggableClassnames.selectionEnd
    )
  });
}
export function CircularView(props) {
  const [limits] = useAnnotationLimits();
  let [rotationRadians, setRotationRadians] = useState(0);
  let [_zoomLevel, setZoomLevel] = useState(1);
  if (props.circ_zoomLevel !== void 0) {
    rotationRadians = props.circ_rotationRadians;
    setRotationRadians = props.circ_setRotationRadians;
    _zoomLevel = props.circ_zoomLevel;
    setZoomLevel = props.circ_setZoomLevel;
  }
  const { sequenceData = {}, smallSlider } = props;
  const { sequence = "atgc", circular } = sequenceData;
  const sequenceLength = sequenceData.noSequence ? sequenceData.size : sequenceData.sequence.length;
  const hasZoomableLength = sequenceLength >= 50;
  const hasRotateableLength = sequenceLength >= 10;
  if (!hasZoomableLength) _zoomLevel = 1;
  if (!hasRotateableLength) rotationRadians = 0;
  const circRef = useRef();
  const rotateHelper = useRef({});
  const zoomHelper = useRef({});
  useEffect(() => {
    rotateHelper.current.triggerChange && rotateHelper.current.triggerChange(({ changeValue }) => {
      changeValue(
        normalizeAngle(Math.PI * 2 - rotationRadians) / Math.PI * 180
      );
    });
  }, []);
  let zoomLevel = _zoomLevel;
  let smallZoom = 1;
  if (_zoomLevel < 1) {
    smallZoom = _zoomLevel;
    zoomLevel = 1;
  }
  const {
    //set defaults for all of these vars
    width = 400,
    height = 400,
    noRedux,
    readOnly,
    hideName = false,
    editorName,
    showAminoAcidUnitAsCodon,
    smartCircViewLabelRender,
    showCicularViewInternalLabels,
    withRotateCircularView: _withRotateCircularView,
    withZoomCircularView,
    selectionLayer = { start: -1, end: -1 },
    annotationHeight = 15,
    spaceBetweenAnnotations = 2,
    annotationVisibility = {},
    annotationLabelVisibility = {},
    caretPosition = -1,
    editorClicked = noop,
    backgroundRightClicked = noop,
    searchLayers = [],
    additionalSelectionLayers = [],
    maxAnnotationsToDisplay,
    searchLayerRightClicked = noop,
    selectionLayerRightClicked = noop,
    searchLayerClicked = noop,
    instantiated,
    noWarnings,
    labelLineIntensity,
    fontHeightMultiplier,
    hoveredId,
    labelSize,
    nameFontSizeCircularView = 14,
    fullScreen
  } = props;
  const withRotateCircularView = _withRotateCircularView || withZoomCircularView;
  const sequenceName = hideName ? "" : sequenceData.name || "";
  let annotationsSvgs = [];
  let labels = {};
  const { isProtein } = sequenceData;
  const maxZoomLevel = Math.max(5, Math.floor(sequenceLength / 100));
  const svgWidth = Math.max(Number(width) || 300, 0);
  const svgHeight = Math.max(Number(height) || 300, 0);
  const percentOfCircle = 1 / zoomLevel * Math.min(svgWidth, 800) / 800;
  const isZoomedIn = zoomLevel !== 1;
  let radius = BASE_RADIUS;
  const rotation = 2 * Math.PI - rotationRadians;
  let rangeToShowLength = sequenceLength;
  let rangeToShow = { start: 0, end: Math.max(sequenceLength - 1, 0) };
  let visibleAngleRange;
  if (isZoomedIn) {
    const visibleAngle = Math.PI * percentOfCircle;
    radius = Math.max(BASE_RADIUS, svgWidth / Math.sin(visibleAngle / 2) / 2);
    visibleAngleRange = normalizeAngleRange({
      start: rotation - visibleAngle / 2,
      end: rotation + visibleAngle / 2
    });
    const rangeToShowStart = getPositionFromAngle(
      visibleAngleRange.start,
      sequenceLength
    );
    const rangeToShowEnd = getPositionFromAngle(
      visibleAngleRange.end,
      sequenceLength
    );
    rangeToShow = {
      start: normalizePositionByRangeLength(rangeToShowStart, sequenceLength),
      end: normalizePositionByRangeLength(rangeToShowEnd, sequenceLength)
    };
    rangeToShowLength = getRangeLength(rangeToShow, sequenceLength);
  }
  const innerRadius = radius - 10;
  const initialRadius = radius;
  const showSeq = isZoomedIn && rangeToShowLength < 140;
  const showSeqText = rangeToShowLength < 80;
  const layersToDraw = [
    { zIndex: 10, layerName: "sequenceChars" },
    {
      zIndex: 20,
      layerName: "features",
      isAnnotation: true,
      annotations: Object.values(
        sequenceData.filteredFeatures || sequenceData.features || {}
      ).filter((f) => f.type !== "restriction_site"),
      // spaceBefore: 10,
      spaceAfter: 5
    },
    ...annotationVisibility.axis ? [
      {
        layerName: "axisNumbers",
        annotationType: "axisNumbers",
        Annotation: AxisNumbers,
        isAnnotation: true,
        noTitle: true,
        noHover: true,
        annotations: calculateTickMarkPositionsForGivenRange({
          increaseOffset: true,
          range: rangeToShow,
          tickSpacing: rangeToShowLength < 10 ? 2 : rangeToShowLength < 50 ? Math.ceil(rangeToShowLength / 25) * 5 : Math.ceil(rangeToShowLength / 100) * 10,
          sequenceLength,
          isProtein
        }).map((pos) => {
          return {
            name: "Tick Mark",
            tickPosition: pos,
            start: pos,
            end: pos
          };
        }),
        annotationProps: {
          hideTicks: !annotationVisibility.axis,
          hideNumbers: !annotationVisibility.axisNumbers
        },
        useCenter: true,
        passAnnotation: true,
        allOnSameLevel: true,
        addHeight: true,
        alwaysShow: true,
        showLabels: false,
        spaceBefore: 0,
        spaceAfter: 0
      },
      {
        zIndex: 0,
        layerName: "axis",
        Annotation: Axis,
        spaceBefore: 10,
        spaceAfter: 5
      }
    ] : [],
    {
      zIndex: 10,
      layerName: "cutsites",
      fontStyle: "italic",
      Annotation: Cutsite,
      hideAnnotation: showSeqText,
      useStartAngle: true,
      allOnSameLevel: true,
      noHeight: true,
      addHeight: false,
      positionBy: positionCutsites,
      isAnnotation: true
    },
    ...showSeq && annotationVisibility.sequence ? [
      {
        layerName: "dnaSequences",
        annotationType: "dnaSequences",
        noTitle: true,
        Annotation: CircularDnaSequence,
        isAnnotation: true,
        annotationProps: {
          showReverseSequence: annotationVisibility.reverseSequence,
          showDnaColors: annotationVisibility.dnaColors,
          showSeqText
        },
        noHover: true,
        annotations: getSequenceWithinRange(rangeToShow, sequence).split("").map((letter, i) => {
          const pos = rangeToShow.start + i;
          return {
            className: `ve-dna-letter-${pos}`,
            start: pos,
            end: pos,
            letter
          };
        }),
        useCenter: true,
        passAnnotation: true,
        allOnSameLevel: true,
        addHeight: true,
        alwaysShow: true,
        showLabels: false,
        spaceBefore: 5,
        spaceAfter: annotationVisibility.reverseSequence ? 20 : 5
      }
    ] : [],
    {
      zIndex: 5,
      alwaysShow: true,
      layerName: "rowViewVisibleRange",
      Annotation: drawRowViewVisibleRange
    },
    {
      zIndex: 15,
      alwaysShow: true,
      layerName: "caret",
      Annotation: drawCaret
    },
    {
      zIndex: 10,
      alwaysShow: true,
      layerName: "selectionLayer",
      Annotation: drawSelectionLayer,
      spaceAfter: 10
    },
    {
      zIndex: 20,
      layerName: "replacementLayers",
      isAnnotation: true,
      spaceAfter: 20
    },
    {
      zIndex: 20,
      layerName: "deletionLayers",
      isAnnotation: true,
      spaceAfter: 20
    },
    {
      zIndex: 20,
      Annotation: Orf,
      showLabels: false,
      getColor: getOrfColor,
      layerName: "orfs",
      isAnnotation: true,
      spaceBefore: 10
    },
    {
      spaceBefore: 10,
      spaceAfter: 5,
      zIndex: 20,
      Annotation: Primer,
      isAnnotation: true,
      layerName: "primers"
    },
    {
      zIndex: 20,
      layerName: "parts",
      isAnnotation: true,
      spaceBefore: 10,
      spaceAfter: 5
    },
    {
      zIndex: 20,
      layerName: "lineageAnnotations",
      isAnnotation: true,
      spaceBefore: 10,
      spaceAfter: 5
    },
    {
      zIndex: 20,
      layerName: "assemblyPieces",
      isAnnotation: true,
      spaceBefore: 10,
      spaceAfter: 5
    },
    {
      zIndex: 20,
      layerName: "warnings",
      isAnnotation: true,
      spaceBefore: 10,
      spaceAfter: 5
    },
    {
      zIndex: 30,
      alwaysShow: true,
      layerName: "labels",
      Annotation: Labels,
      circularViewWidthVsHeightRatio: width / height,
      passLabels: true,
      labelLineIntensity,
      labelSize,
      fontHeightMultiplier,
      textScalingFactor: 700 / Math.min(width, height)
    }
  ];
  const paredDownMessages = [];
  const output = layersToDraw.map((layer) => {
    const {
      layerName,
      maxToDisplay,
      Comp,
      Annotation,
      fontStyle,
      alwaysShow,
      isAnnotation,
      spaceBefore = 0,
      spaceAfter = 0,
      zIndex,
      passLabels,
      noTitle,
      ...rest
    } = layer;
    if (!(alwaysShow || annotationVisibility[layerName])) {
      return null;
    }
    let comp;
    let results;
    const singularName = getSingular(layerName);
    const nameUpper = upperFirst(layerName);
    radius += spaceBefore;
    const sharedProps = {
      radius,
      innerRadius: BASE_RADIUS,
      outerRadius: radius,
      noRedux,
      isProtein,
      smartCircViewLabelRender,
      extraSideSpace: Math.max(0, width - height),
      onClick: props[singularName + "Clicked"],
      onDoubleClick: props[singularName + "DoubleClicked"],
      onRightClicked: props[singularName + "RightClicked"],
      sequenceLength,
      editorName,
      showCicularViewInternalLabels,
      ...rest
    };
    if (isAnnotation) {
      if (!map(sequenceData[layerName]).length && !alwaysShow) {
        radius -= spaceBefore;
        return null;
      }
      const trimmedAnnotations = flatMap(
        layer.annotations || sequenceData["filtered" + nameUpper] || sequenceData[layerName] || [],
        (range) => {
          const overlapOfRanges = getOverlapsOfPotentiallyCircularRanges(
            range,
            rangeToShow,
            sequenceLength,
            true
          );
          if (!overlapOfRanges || !overlapOfRanges.length) return [];
          return { ...range };
        }
      );
      const maxToShow = !isZoomedIn && (maxAnnotationsToDisplay && maxAnnotationsToDisplay[layerName] ? maxAnnotationsToDisplay[layerName] : layerName === "features" ? 1e4 : limits[layerName] || 1e4);
      const [trimmedAndParedAnns, paredDown] = maxToShow ? pareDownAnnotations(trimmedAnnotations, maxToShow) : [trimmedAnnotations];
      if (paredDown) {
        paredDownMessages.push(
          getParedDownWarning({
            nameUpper,
            maxToShow,
            isAdjustable: !maxAnnotationsToDisplay
          })
        );
      }
      results = drawAnnotations({
        readOnly,
        noTitle,
        rotationRadians,
        visibleAngleRange,
        isZoomedIn,
        Annotation: Annotation || Comp || Feature,
        fontStyle,
        hoveredId,
        annotationType: singularName,
        type: singularName,
        reverseAnnotations: true,
        showLabels: !(annotationLabelVisibility[layerName] === false),
        annotations: trimmedAndParedAnns,
        annotationHeight,
        spaceBetweenAnnotations,
        ...sharedProps,
        ...props[singularName + "Options"]
      });
    } else {
      results = Annotation({
        rotationRadians,
        ...passLabels && { labels },
        ...sharedProps
      });
    }
    if (results) {
      radius += results.height || 0;
      const newLabels = map(results.labels || {});
      if (layerName === "restrictionSites" && newLabels.length > 0) {
        console.log("[DEBUG] Collected labels from restrictionSites:", newLabels);
      }
      labels = [...map(labels), ...newLabels];
      comp = results.component === void 0 ? results : results.component;
    }
    radius += spaceAfter;
    return {
      result: comp,
      zIndex
    };
  }).filter(function(i) {
    return !!i;
  });
  annotationsSvgs = sortBy(output, "zIndex").reduce(function(arr, { result }) {
    return arr.concat(result);
  }, []);
  function drawRowViewVisibleRange() {
    const { rowViewVisibleRange } = props;
    const rangeLen = rowViewVisibleRange ? getRangeLength(rowViewVisibleRange, sequenceLength) : 0;
    const isShowing = rowViewVisibleRange && rowViewVisibleRange.start > -1 && rowViewVisibleRange.end > -1 && sequenceLength > 0 && rangeLen < sequenceLength;
    if (isShowing) {
      return /* @__PURE__ */ jsx(
        SelectionLayer,
        {
          ...{
            isDraggable: false,
            selectionLayer: {
              ...rowViewVisibleRange,
              color: "rgba(0, 255, 0, 0.2)",
              className: "veRowViewVisibleRange",
              hideCarets: true
            },
            sequenceLength,
            radius,
            innerRadius
          }
        },
        "veRowViewVisibleRange"
      );
    }
    return null;
  }
  function drawSelectionLayer() {
    return getAllSelectionLayers({
      additionalSelectionLayers,
      searchLayers,
      selectionLayer
    }).map(function(selectionLayer2, index) {
      if (selectionLayer2.start >= 0 && selectionLayer2.end >= 0 && sequenceLength > 0) {
        return /* @__PURE__ */ jsx(
          SelectionLayer,
          {
            ...{
              index,
              showAminoAcidUnitAsCodon,
              isDraggable: true,
              isProtein,
              selectionLayer: selectionLayer2,
              onRightClicked: selectionLayer2.isSearchLayer ? searchLayerRightClicked : selectionLayerRightClicked,
              onClick: selectionLayer2.isSearchLayer ? searchLayerClicked : void 0,
              sequenceLength,
              radius,
              innerRadius
            }
          },
          "veCircularViewSelectionLayer" + index
        );
      } else {
        return null;
      }
    }).filter((el) => {
      return !!el;
    });
  }
  function drawCaret() {
    if (caretPosition !== -1 && selectionLayer.start < 0 && sequenceLength >= 0) {
      return /* @__PURE__ */ jsx(
        Caret,
        {
          ...{
            caretPosition,
            showAminoAcidUnitAsCodon,
            sequenceLength,
            isProtein,
            innerRadius,
            outerRadius: radius
          }
        },
        "veCircularViewCaret"
      );
    }
  }
  if (radius < 150) radius = 150;
  const widthToUse = Math.max(Number(width) || 300);
  const heightToUse = Math.max(Number(height) || 300);
  const proteinUnits = showAminoAcidUnitAsCodon ? "codons" : "AAs";
  const bpTitle = isProtein ? `${Math.floor(sequenceLength / 3)} ${proteinUnits}` : `${sequenceLength} bps`;
  const nameEl = /* @__PURE__ */ jsx(
    "div",
    {
      className: "veSequenceName",
      style: {
        position: "absolute",
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        display: "flex",
        justifyContent: "center",
        alignItems: isZoomedIn || smallZoom < 1 ? "end" : "center",
        paddingLeft: isZoomedIn || smallZoom < 1 ? 20 : 0,
        paddingRight: isZoomedIn || smallZoom < 1 ? 20 : 0,
        textAlign: "center",
        zIndex: 1
      },
      children: /* @__PURE__ */ jsxs("div", { style: { marginBottom: isZoomedIn || smallZoom < 1 ? 80 : 0 }, children: [
        /* @__PURE__ */ jsx(
          "div",
          {
            title: sequenceName,
            className: "veCircularViewTextWrapper",
            style: {
              textAlign: "center",
              display: "-webkit-box",
              WebkitLineClamp: "3",
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              wordBreak: "break-all",
              textOverflow: "ellipsis",
              width: isZoomedIn || smallZoom < 1 ? void 0 : innerRadius,
              maxHeight: innerRadius - 15,
              fontSize: nameFontSizeCircularView
            },
            children: sequenceName
          }
        ),
        /* @__PURE__ */ jsx("span", { title: bpTitle, style: { fontSize: 10 }, children: bpTitle })
      ] })
    }
  );
  const target = React.useRef();
  usePinch(
    ({ delta: [d], event }) => {
      if (d === 0) return;
      event.stopPropagation();
      zoomHelper.current.triggerChange && zoomHelper.current.triggerChange(({ changeValue, value }) => {
        changeValue(value + d * 5);
      });
    },
    {
      target,
      from: [zoomLevel]
    }
  );
  const toPass = { ...props, sequenceLength, circRef, rotationRadians };
  return /* @__PURE__ */ jsxs(
    "div",
    {
      ref: target,
      style: {
        width: widthToUse,
        height: heightToUse,
        position: "relative",
        overflow: "hidden"
      },
      onWheel: withZoomCircularView && hasRotateableLength ? (e) => {
        let delta = e.deltaY;
        if (Math.abs(e.deltaY) < Math.abs(e.deltaX)) {
          delta = e.deltaX;
        }
        if (e.ctrlKey || e.metaKey) {
          e.preventDefault();
          e.stopPropagation();
          if (zoomHelper.current && zoomHelper.current.triggerChange) {
            zoomHelper.current.triggerChange(({ changeValue, value }) => {
              const zoomDelta = delta < 0 ? 0.5 : -0.5;
              const newZoomLevel = Math.max(1, Math.min(maxZoomLevel, value + zoomDelta));
              changeValue(newZoomLevel);
            });
          }
          return;
        }
        rotateHelper.current.triggerChange && rotateHelper.current.triggerChange(({ changeValue, value }) => {
          changeValue(
            normalizeAngle((value + delta / 4) * Math.PI / 180) / Math.PI * 180
          );
        });
        e.stopPropagation();
      } : void 0,
      className: classNames("veCircularView", props.className),
      children: [
        !hideName && isZoomedIn && nameEl,
        withRotateCircularView && hasRotateableLength && /* @__PURE__ */ jsx(
          RotateCircularViewSlider,
          {
            bindOutsideChangeHelper: rotateHelper.current,
            zoomLevel,
            smallSlider,
            maxZoomLevel,
            setRotationRadians,
            editorName
          }
        ),
        withZoomCircularView && hasZoomableLength && /* @__PURE__ */ jsx(
          ZoomCircularViewSlider,
          {
            zoomHelper,
            smallSlider,
            onZoom: () => {
              const caret = caretPosition > -1 ? caretPosition : selectionLayer.start > -1 ? getMiddleOfRange(selectionLayer, sequenceLength) : void 0;
              if (caret !== void 0) {
                const radToRotateTo = caret / sequenceLength * Math.PI * 2;
                rotateHelper.current.triggerChange && rotateHelper.current.triggerChange(({ changeValue }) => {
                  const isInView = isRangeOrPositionWithinRange(
                    caret,
                    rangeToShow,
                    sequenceLength
                  );
                  if (!isInView) {
                    if (selectionLayer.start > -1) {
                      const trimmed = trimRangeByAnotherRange(
                        selectionLayer,
                        rangeToShow,
                        sequenceLength
                      );
                      if (trimmed.start !== selectionLayer.start || trimmed.end !== selectionLayer.end)
                        return;
                    }
                    changeValue(radToRotateTo / Math.PI * 180);
                  }
                });
              }
            },
            zoomLevel: _zoomLevel,
            maxZoomLevel,
            setZoomLevel
          }
        ),
        /* @__PURE__ */ jsx(
          Draggable,
          {
            bounds: { top: 0, left: 0, right: 0, bottom: 0 },
            onDrag: (event) => {
              getNearestCursorPositionToMouseEvent(event, toPass, editorDragged);
            },
            onStart: (event) => {
              getNearestCursorPositionToMouseEvent(
                event,
                toPass,
                editorDragStarted
              );
            },
            onStop: editorDragStopped,
            children: /* @__PURE__ */ jsxs(
              "div",
              {
                ref: withZoomCircularView && hasZoomableLength ? target : void 0,
                children: [
                  /* @__PURE__ */ jsx(
                    "svg",
                    {
                      onClick: (event) => {
                        instantiated && getNearestCursorPositionToMouseEvent(
                          event,
                          toPass,
                          editorClicked
                        );
                      },
                      onContextMenu: (e) => {
                        getNearestCursorPositionToMouseEvent(
                          e,
                          toPass,
                          backgroundRightClicked
                        );
                      },
                      style: {
                        overflow: "visible",
                        display: "block"
                      },
                      className: "circularViewSvg",
                      viewBox: isZoomedIn ? `${-svgWidth / 2 / smallZoom},${-svgHeight / 2 / smallZoom - (!isZoomedIn ? 0 : initialRadius + BASE_RADIUS * 2 - 100)},${svgWidth / smallZoom},${svgHeight / smallZoom}` : `-${radius} -${radius} ${radius * 2} ${radius * 2}`,
                      width: svgWidth,
                      height: svgHeight,
                      children: /* @__PURE__ */ jsxs("g", { children: [
                        /* @__PURE__ */ jsx(
                          "circle",
                          {
                            ref: circRef,
                            r: radius,
                            style: { opacity: 0, zIndex: -1 },
                            className: "veHiddenAxis"
                          }
                        ),
                        annotationsSvgs,
                        !hideName && !isZoomedIn && /* @__PURE__ */ jsx(
                          "foreignObject",
                          {
                            x: -72.5 / 2,
                            y: -72.5 / 2,
                            width: 72.5,
                            height: 72.5,
                            transform: `rotate(${-rotationRadians * 180 / Math.PI})`,
                            children: /* @__PURE__ */ jsxs(
                              "div",
                              {
                                xmlns: "http://www.w3.org/1999/xhtml",
                                className: "veCircularViewMiddleOfVectorText",
                                children: [
                                  /* @__PURE__ */ jsx(
                                    "div",
                                    {
                                      title: sequenceName,
                                      className: "veCircularViewTextWrapper",
                                      style: {
                                        display: "-webkit-box",
                                        WebkitLineClamp: "3",
                                        WebkitBoxOrient: "vertical",
                                        overflow: "hidden",
                                        wordBreak: "break-all",
                                        textOverflow: "ellipsis",
                                        width: 72.5,
                                        maxHeight: 72.5 - 15,
                                        fontSize: nameFontSizeCircularView
                                      },
                                      children: sequenceName
                                    }
                                  ),
                                  /* @__PURE__ */ jsx("span", { title: bpTitle, style: { fontSize: 10 }, children: bpTitle })
                                ]
                              },
                              "circViewSvgCenterText"
                            )
                          }
                        )
                      ] })
                    },
                    "circViewSvg"
                  ),
                  /* @__PURE__ */ jsxs(VeTopRightContainer, { ...{ fullScreen }, children: [
                    !circular && !noWarnings && /* @__PURE__ */ jsx(
                      VeWarning,
                      {
                        "data-test": "ve-warning-circular-to-linear",
                        intent: "warning",
                        tooltip: "Warning! You're viewing a linear sequence in the Circular Map. Click on 'Linear Map' to view the linear sequence in a more intuitive way."
                      },
                      "ve-warning-circular-to-linear"
                    ),
                    !noWarnings && paredDownMessages,
                    isZoomedIn && /* @__PURE__ */ jsx(
                      CircularZoomMinimap,
                      {
                        rotationRadians,
                        percentOfCircle
                      }
                    )
                  ] })
                ]
              }
            )
          }
        )
      ]
    }
  );
}
export default withEditorInteractions(CircularView);
